import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types/domain';

interface ProtectedRouteProps {
  /** If set, user must have at least one of these roles to access. */
  roles?: UserRole[];
  /** Where to send unauthenticated users; preserves intended destination. */
  redirectTo?: string;
}

/**
 * Wrap a route subtree to require authentication (and optionally a role).
 *
 *   <Route element={<ProtectedRoute roles={['ADMIN']} />}>...</Route>
 *
 * - While the initial /me probe is in flight we render nothing — avoids a
 *   login-redirect flash for already-authenticated users on hard refresh.
 * - Anonymous users get redirected to /auth/login with the original location
 *   pinned to state so the login handler can bounce them back.
 */
export function ProtectedRoute({ roles, redirectTo = '/auth/login' }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();

  if (isLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (roles && roles.length > 0 && !hasRole(...roles)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
